import { Heading, Line } from '@mm/components';
import { ContactsFilterIcon, PlusCircleIcon } from '@mm/icons';
import { ErrorReportingUtils } from '@mm/plugins';
import { Variable } from '@mm/state/interfaces/audiences';
import {
  FILTER_TYPES,
  FilterParams,
  TagFilterItem,
} from '@mm/state/model/AudiencesModel';
import { selectAudienceVariables } from '@mm/state/selectors/audienceSelector';
import { selectAudienceByIdOrIds } from '@mm/state/selectors/messengerAdSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { getUniqId } from '@mm/utils';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { LocalFilterItem } from '../../../../../contacts/sharedFilterTypes';
import { useAudienceFiltersHandlers } from '../use-audience-filters-handlers';
import './AudienceActiveFilters.pcss';
import { FilterPreview } from './FilterPreview/FilterPreview';

type Props = {
  className?: string;
  audienceId: number;
  onAddFilterBtnClick?: () => void;
  onEditFilterBtnClick?: (filter: LocalFilterItem | LocalTagFilterItem) => void;
  onDeleteFilterSuccess: () => void;
};

export type LocalTagFilterItem = TagFilterItem & {
  filterId: string;
};

const mapAudienceFilterParams = (
  filterParams?: FilterParams,
  variables?: Variable[],
): LocalFilterItem[] => {
  let filters: any[] = [];
  if (filterParams && variables) {
    const filterKeys = Object.keys(filterParams);
    filters = filterKeys
      .filter(key => key !== 'logic')
      .map(key => {
        const filter = filterParams[key];

        if (filter.filterType === FILTER_TYPES.variableValues) {
          const currentVariable = variables.find(
            v => v.variableId === filter.variableId,
          );
          const variableName = currentVariable
            ? currentVariable.name
            : 'Unknown Variable';

          if (!currentVariable) {
            ErrorReportingUtils.reportError(
              'Missing variable for audience filter',
              {
                extraData: {
                  filter,
                  variables,
                },
              },
            );
          }

          return {
            ...filter,
            filterId: key,
            variable: variableName,
          };
        }

        return {
          ...filter,
          filterId: key,
        };
      });
  }
  return filters;
};

export const AudienceActiveFilters = ({
  className,
  audienceId,
  onAddFilterBtnClick,
  onEditFilterBtnClick = () => {},
  onDeleteFilterSuccess = () => {},
}: Props) => {
  const { deleteFilterItemById, deleteTagFilterItemById } =
    useAudienceFiltersHandlers(audienceId);

  const variables = useSelector(selectAudienceVariables);
  const [audience] = useSelector((state: StoreState) =>
    selectAudienceByIdOrIds(state, audienceId),
  );

  const { filterParams, tagsFilters, systemAudience, usedInActiveDrip } =
    audience || {};

  const readOnly = systemAudience || usedInActiveDrip;

  const filters = mapAudienceFilterParams(filterParams, variables);

  const tags: LocalTagFilterItem[] = Object.entries(tagsFilters || {}).map(
    ([id, tag]) => ({
      filterId: id,
      ...tag,
    }),
  );

  const onFilterDelete = async (filter: LocalFilterItem) => {
    await deleteFilterItemById(Number(filter.filterId));
    onDeleteFilterSuccess();
  };

  const onTagFilterDelete = async (tagFilter: LocalTagFilterItem) => {
    await deleteTagFilterItemById(Number(tagFilter.filterId));
    onDeleteFilterSuccess();
  };

  const { logic: _, ...paramsNoLogic } = filterParams || {};
  const isEmptyState =
    !audience || (isEmpty(paramsNoLogic) && isEmpty(tagsFilters));

  return (
    <div className={classNames(className, 'audience-active-filters')}>
      <Heading
        size={4}
        text="All contacts who..."
        className="audience-active-filters__heading"
      />
      {isEmptyState && (
        <ContactsFilterIcon className="audience-active-filters__contacts-icon" />
      )}

      {filters.map((filter, i) => (
        <>
          {i !== 0 && <Line text="AND" />}
          <FilterPreview<LocalFilterItem>
            key={getUniqId(String(filter.filterId))}
            filter={filter}
            readOnly={readOnly}
            onDelete={onFilterDelete}
            onEdit={() => onEditFilterBtnClick(filter)}
          />
        </>
      ))}

      {tags.map((tagFilter, i, a) => (
        <>
          {i + filters.length !== 0 && i !== a.length && <Line text="AND" />}
          <FilterPreview<LocalTagFilterItem>
            key={getUniqId(tagFilter.filterId)}
            filter={tagFilter}
            readOnly={readOnly}
            onDelete={onTagFilterDelete}
            onEdit={() => onEditFilterBtnClick(tagFilter)}
          />
        </>
      ))}

      <PrimaryButton
        className={classNames(
          'audience-active-filters__add-filter-button',
          className,
        )}
        leftIcon={<PlusCircleIcon />}
        onClick={onAddFilterBtnClick}
        disabled={usedInActiveDrip}
      >
        Add filter
      </PrimaryButton>
    </div>
  );
};
